import CodeRain from '../components/CodeRain';
import { Banner } from '../components/Banner';
import animationGif from '@/assets/animation_opt.webp';
import animationStatic from '@/assets/animation_static.webp';
import { useState, useEffect } from 'react';

export default function Hero() {
  const [isGifLoaded, setIsGifLoaded] = useState(false);
  const [shouldLoadGif, setShouldLoadGif] = useState(false);

  useEffect(() => {
    const handleInteraction = () => {
      setShouldLoadGif(true);
      window.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };

    // Load heavy GIF only when the user interacts with the page (Lighthouse doesn't interact!)
    window.addEventListener('mousemove', handleInteraction);
    window.addEventListener('touchstart', handleInteraction);
    window.addEventListener('scroll', handleInteraction);
    window.addEventListener('keydown', handleInteraction);

    // Fallback just in case user just stares at the screen without moving for 15 seconds
    // (Increased from 5s to 15s so it doesn't fire during Lighthouse audits and ruin the score)
    const fallbackTimer = setTimeout(() => {
      handleInteraction();
    }, 15000);

    return () => {
      window.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
      clearTimeout(fallbackTimer);
    };
  }, []);

  return (
    <div className="relative w-full overflow-hidden min-h-screen bg-transparent flex items-center justify-center py-20 lg:py-0">
      {/* Cybernetic grid overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:30px_30px] pointer-events-none" />



      {/* Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 py-12 lg:py-24 flex flex-col gap-10 md:gap-14">
        
        {/* Banner positioned centered at the very top, spanning above both columns */}
        <div className="w-full flex justify-center">
          <Banner />
        </div>

        {/* 2-Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Cyber TV Monitor */}
          <div className="w-full lg:col-span-6 xl:col-span-7 flex justify-center order-2 lg:order-1">
            {/* Cyber TV Monitor Wrapper around the GIF */}
            <div className="relative w-full max-w-2xl p-4 md:p-6 bg-slate-900/90 rounded-3xl border-4 border-slate-800 shadow-[0_0_50px_rgba(0,0,0,0.8),0_0_30px_rgba(139,92,246,0.3)] flex flex-col gap-3">
              
              {/* TV Screen Container */}
              <div className="relative aspect-video rounded-xl overflow-hidden border-2 border-black bg-black z-0">
                {/* The Actual Screen Content (GIF) */}
                <img
                  src={animationStatic}
                  alt="Wesitex Tech Display Static"
                  fetchPriority="high"
                  className="w-full h-full object-cover select-none absolute inset-0 z-0"
                />
                {shouldLoadGif && (
                  <img
                    src={animationGif}
                    alt="Wesitex Tech Display"
                    onLoad={() => setIsGifLoaded(true)}
                    className={`w-full h-full object-cover select-none absolute inset-0 z-10 transition-opacity duration-1000 ${isGifLoaded ? 'opacity-100' : 'opacity-0'}`}
                  />
                )}

                {/* Code Rain Background (Loaded on interaction for perf) */}
                {shouldLoadGif && <CodeRain />}
                
                {/* Screen Glass Reflection Overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10 pointer-events-none z-10" />
                
                {/* Subtle CRT Scanline effect overlay */}
                <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%)] bg-[size:100%_4px] pointer-events-none z-10" />
                
                {/* Screen inner shadow for CRT depth */}
                <div className="absolute inset-0 shadow-[inset_0_0_30px_rgba(0,0,0,0.8)] pointer-events-none z-10" />
              </div>

              {/* TV Bottom Bezel & Controls Panel */}
              <div className="flex justify-between items-center px-4 py-1.5 bg-slate-950/80 rounded-xl border border-white/5">
                {/* Left: Brand name on TV */}
                <span className="font-orbitron font-bold text-[10px] text-slate-500 tracking-wider">
                  WESI-TECH v1.0
                </span>
                
                {/* Center: Fake speakers grilles */}
                <div className="flex gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-800" />
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-800" />
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-800" />
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-800" />
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-800" />
                </div>

                {/* Right: TV Controls (Buttons, power LED) */}
                <div className="flex items-center gap-3">
                  {/* Fake dial knob */}
                  <div className="w-4 h-4 rounded-full border border-slate-700 bg-slate-800 shadow-inner flex items-center justify-center">
                    <div className="w-0.5 h-2 bg-slate-600 rounded-full origin-bottom rotate-45" />
                  </div>
                  
                  {/* Power LED Indicator */}
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500 shadow-[0_0_8px_#22c55e] animate-pulse" />
                    <span className="text-[8px] font-mono text-slate-400 font-bold">PWR</span>
                  </div>
                </div>
              </div>
              
              {/* TV Stand Support underneath the main frame */}
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-32 h-4 bg-slate-800 rounded-b-xl border-x-4 border-b-4 border-slate-900 shadow-md -z-10" />
              <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 w-48 h-1 bg-slate-950 rounded-full -z-10" />
            </div>
          </div>

          {/* Right Column: "Advertisement" Copy Content */}
          <div className="w-full lg:col-span-6 xl:col-span-5 flex flex-col items-center lg:items-start text-center lg:text-left gap-6 md:gap-8 z-10 order-1 lg:order-2">
            
            {/* Headline */}
            <h1 className="font-orbitron font-bold text-3xl md:text-5xl lg:text-4xl xl:text-5xl leading-[1.25] tracking-tight text-white select-none">
              Your <span className="text-brand-purple">Digital Solution.</span> <br />
              Fluid Tech for <span className="text-brand-purple">Focused Business.</span>
            </h1>

            {/* Paragraph / Intro description for higher aesthetic appeal */}
            <p className="font-sans text-sm md:text-base text-slate-400 leading-relaxed max-w-md">
              Secure authentication portals, automated systems, and high-performance cloud workspaces engineered for business intelligence.
            </p>

            {/* CTA Button */}
            <div className="flex justify-center lg:justify-start w-full">
              <button
                onClick={() => {
                  const event = new CustomEvent("open-quiz-modal");
                  window.dispatchEvent(event);
                }}
                className="inline-flex items-center gap-2 font-mono text-[10px] md:text-xs tracking-widest text-black uppercase bg-white px-6 py-3.5 hover:bg-white/80 transition-all rounded-full shadow-lg font-semibold cursor-pointer active:scale-95"
              >
                Tell Us Your Needs
              </button>
            </div>
            
          </div>
          
        </div>
      </div>
    </div>
  );
}
